import { useTranslation } from 'react-i18next';
import { BudgetIcon } from '@/common/components/BudgetIcon/BudgetIcon';
import { Card } from '@/common/components/Card/Card';
import { Grid } from '@/common/components/Grid/Grid';
import { Heading } from '@/common/components/Heading/Heading';
import { Label } from '@/common/components/Label/Label';
import { LabeledValue } from '@/common/components/LabeledValue/LabeledValue';
import { ProgressBar } from '@/common/components/ProgressBar/ProgressBar';
import { SkeletonInline } from '@/common/components/SkeletonInline/SkeletonInline';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { IEmployeeBudget } from '@/common/integrations/menhir/employee/budgets/budgets.types';
import {
  getIsBudgetAvailableSoon,
  shouldTreatBudgetAsExpired,
  useGetBudgetCardDescription,
} from '@/common/utils/budgets';
import { DateUtils } from '@/common/utils/date';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';
import { getBudgetUsagePercentage } from './BudgetDetailCard.utils';

export type TBudgetDetailCardProps = {
  budgetEmployee: IEmployeeBudget;
};

export function BudgetDetailCard({ budgetEmployee }: TBudgetDetailCardProps) {
  const { t } = useTranslation();

  const usagePercentage = getBudgetUsagePercentage(budgetEmployee);
  const isAvailableSoon = getIsBudgetAvailableSoon(budgetEmployee);
  const isExpired = shouldTreatBudgetAsExpired(budgetEmployee);
  const description = useGetBudgetCardDescription(budgetEmployee);
  const endedAt = budgetEmployee.endedAt ?? budgetEmployee.budget.endedAt;

  return (
    <Card plain>
      <Stack gap="lg">
        <Stack horizontal gap="sm" align="center">
          <BudgetIcon
            type={budgetEmployee.budget.logo}
            size={20}
            color="accentSecondary10"
          />
          <Heading level={4}>
            {budgetEmployee.displayName
              ? budgetEmployee.displayName
              : t('dashboard.budgets.details.card.budget_usage')}
          </Heading>
        </Stack>
        <Stack gap="sm">
          <ProgressBar
            bars={[
              {
                percentage: usagePercentage,
                backgroundColor: 'accentSecondary40',
              },
            ]}
            backgroundColor="accentSecondary20"
            direction="reverse"
          />
          {budgetEmployee.budget.paymentMethod === 'payroll' ? (
            <Translate
              parent={Label}
              size="sm"
              i18nKey="dashboard.budgets.details.card.payroll_message"
              values={{
                amount: MoneyUtils.moneyToString(budgetEmployee.budgetAmount),
              }}
            />
          ) : (
            <Stack horizontal gap="sm" align="center" justify="between">
              <BudgetUsedAmount
                employeeBudget={budgetEmployee}
                availableSoon={isAvailableSoon}
              />
              <Translate
                parent={Label}
                size="sm"
                i18nKey={`dashboard.budgets.details.card.${
                  budgetEmployee.budget.withAccrual
                    ? 'total_amount_cumulative'
                    : 'total_amount'
                }`}
                values={{
                  amount: MoneyUtils.moneyToString(
                    budgetEmployee.budget.withAccrual
                      ? budgetEmployee.accumulatedAmount
                      : budgetEmployee.budgetAmount
                  ),
                }}
              />
            </Stack>
          )}
        </Stack>
        <Grid columnBasis="basis-[20rem]" gap="md">
          {!isExpired && (
            <LabeledValue
              label={t('dashboard.budgets.details.card.periodicity')}
              value={description}
            />
          )}
          {!isExpired && (
            <LabeledValue
              label={t('dashboard.budgets.details.card.recurrence.cumulative')}
              value={
                budgetEmployee.budget.withAccrual
                  ? t('general.values.yes')
                  : t('general.values.no')
              }
            />
          )}
          {!isAvailableSoon &&
            budgetEmployee.budget.renewalDate &&
            !isExpired &&
            budgetEmployee.budget.recurrence !== 'one_time' && (
              <LabeledValue
                label={t('dashboard.budgets.details.card.renewal_date')}
                value={DateUtils.formatDate(budgetEmployee.budget.renewalDate)}
              />
            )}
          {!budgetEmployee.budget.renewalDate &&
            !isExpired &&
            budgetEmployee.budget.recurrence === 'one_time' &&
            endedAt && (
              <LabeledValue
                label={t('dashboard.budgets.details.card.expires_on')}
                value={DateUtils.formatDate(endedAt)}
              ></LabeledValue>
            )}
          {isExpired && endedAt && (
            <LabeledValue
              label={t('dashboard.budgets.details.card.expired_on')}
              value={DateUtils.formatDate(endedAt)}
            />
          )}
          {isAvailableSoon && (
            <LabeledValue
              label={t('dashboard.budgets.details.card.available_from')}
              value={DateUtils.formatDate(budgetEmployee.firstTopUpDate)}
            />
          )}
        </Grid>
      </Stack>
    </Card>
  );
}

function BudgetUsedAmount({
  employeeBudget,
  availableSoon = false,
}: {
  employeeBudget: IEmployeeBudget;
  availableSoon?: boolean;
}) {
  const available =
    employeeBudget.pocket?.availableBalance ?? employeeBudget.budgetAmount;

  const treatAsExpired = shouldTreatBudgetAsExpired(employeeBudget);

  if (treatAsExpired) {
    // return (
    //   <Trans
    //     i18nKey={'dashboard.budgets.details.card.amount_spent'}
    //     values={{
    //       amount: money.moneyToString(
    //         money.subtract(employeeBudget.budgetAmount, available)
    //       ),
    //     }}
    //   />
    // ); // TODO: when we have spent amount for expired budgets we can add this back
    return (
      <Translate
        parent={Label}
        i18nKey={'dashboard.budgets.details.card.expired'}
        size="sm"
      />
    );
  }

  if (availableSoon) {
    return (
      <Translate
        parent={Label}
        size="sm"
        i18nKey={'dashboard.budgets.details.card.amount_available_soon'}
        values={{
          amount: MoneyUtils.moneyToString(employeeBudget.budgetAmount),
        }}
      />
    );
  }

  if (available.amount === 0) {
    return (
      <Translate
        parent={Label}
        i18nKey={'dashboard.budgets.details.card.all_spent'}
        size="sm"
      />
    );
  }

  return (
    <Translate
      parent={Label}
      i18nKey={'dashboard.budgets.details.card.amount_available'}
      size="sm"
      values={{
        amount: MoneyUtils.moneyToString(available),
      }}
    />
  );
}

export function BudgetDetailCardSkeleton() {
  return (
    <Card plain>
      <Stack gap="lg">
        <Stack horizontal gap="sm" align="center">
          <SkeletonInline className="h-[2rem] w-[2rem]" />
          <SkeletonInline className="h-[2.4rem] w-[12rem]" />
        </Stack>
        <Stack gap="sm">
          <SkeletonInline className="h-[1rem] w-full" />
          <Stack horizontal gap="sm" align="center" justify="between">
            <SkeletonInline className="h-[2rem] w-[12rem]" />
            <SkeletonInline className="h-[2rem] w-[12rem]" />
          </Stack>
        </Stack>
        <Grid columnBasis="basis-[20rem]" gap="md">
          <SkeletonInline className="h-[4.4rem] w-full" />
          <SkeletonInline className="h-[4.4rem] w-full" />
        </Grid>
      </Stack>
    </Card>
  );
}
